import React from "react";
import { Box, Typography } from "@mui/material";
import DataSourceContainer from "../components/DataSourceContainer";
import theme from "Theme/AppTheme";
import { observer } from "mobx-react";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import {
    ConcatFieldType,
    ConditionRuleType,
    SelectedTargetFields,
    SourceFieldType,
    TargetFieldOptions,
} from "../types";
import {
    arrowStyles,
    fieldContainerStyles,
    fieldStyles,
    selectCustomStyle,
} from "../helpers/componentUtils";
import MapField from "./MapField";

type MapFieldsProps = {
    sourceFields: SourceFieldType[];
    sourceFieldOptions: { label: string; value: string }[];
    conditionRules: ConditionRuleType[];
    concatFields: ConcatFieldType[];
    shouldApplyConditions: boolean;
    shouldConcatenateFields: boolean;
    getUpdatedTargetFields: () => {
        targetFieldOptions: TargetFieldOptions;
        selectedTargetFields: SelectedTargetFields;
    };
    updateSourceFields: (
        fieldId: string,
        key: string,
        value: string | boolean,
        targetFieldId?: string,
    ) => void;
};

const MapFields: React.FC<MapFieldsProps> = observer(
    ({
        sourceFields,
        sourceFieldOptions,
        conditionRules,
        concatFields,
        shouldApplyConditions,
        shouldConcatenateFields,
        updateSourceFields,
        getUpdatedTargetFields,
    }) => {
        const mappedTargets = [
            ...(shouldApplyConditions
                ? conditionRules.map((rule) => ({
                      target: rule.targetFieldName,
                      id: rule.id,
                      type: "Condition Rule",
                  }))
                : []),
            ...(shouldConcatenateFields
                ? concatFields.map((field) => ({
                      target: field.targetFieldName,
                      id: field.id,
                      type: "Concatenation",
                  }))
                : []),
        ];

        const showMappedFields = mappedTargets.length > 0;
        return (
            <DataSourceContainer
                heading="Map Fields"
                subHeading="Select the fields you'd like to map so you can easily search and analyze your data"
            >
                <Box
                    sx={{
                        background: theme.palette.gray[100],
                        borderRadius: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            padding: theme.spacing(2, 2, 0, 2),
                        }}
                    >
                        <Typography
                            sx={{
                                flex: 16,
                                fontWeight: 600,
                                fontSize: 14,
                                marginLeft: 1,
                            }}
                        >
                            Source
                        </Typography>
                        <Typography
                            sx={{ flex: 15, fontWeight: 600, fontSize: 14 }}
                        >
                            Target
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            ...fieldContainerStyles,
                            padding: 2,
                        }}
                    >
                        {sourceFields.map((sourceField) => {
                            return (
                                <MapField
                                    key={sourceField.id}
                                    sourceField={sourceField}
                                    sourceFieldOptions={sourceFieldOptions}
                                    getUpdatedTargetFields={
                                        getUpdatedTargetFields
                                    }
                                    updateSourceFields={updateSourceFields}
                                />
                            );
                        })}
                    </Box>
                    {showMappedFields && (
                        <Box
                            sx={{
                                padding: 2,
                                marginTop: 1,
                                borderTop: `1px solid ${theme.palette.gray[400]}`,
                            }}
                        >
                            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                                Mapped in Conditional
                            </Typography>
                            <Box
                                sx={{
                                    ...fieldContainerStyles,
                                    marginTop: 2,
                                }}
                            >
                                {mappedTargets.map(({ target, id, type }) => (
                                    <Box
                                        key={id}
                                        sx={{
                                            ...fieldStyles,
                                            'div[class*="field"]': {
                                                background:
                                                    theme.palette.gray[100],
                                            },
                                        }}
                                    >
                                        <AcxSelect
                                            customStyle={selectCustomStyle({
                                                flex: 15,
                                            })}
                                            fullWidth
                                            id={`mapped-target-${id}`}
                                            options={[]}
                                            defaultValue={{
                                                label: `${target} - ${type}`,
                                                value: "",
                                            }}
                                            isDisabled
                                        />
                                        <ArrowForwardOutlinedIcon
                                            sx={arrowStyles}
                                        />
                                        <AcxSelect
                                            customStyle={selectCustomStyle({
                                                flex: 15,
                                            })}
                                            id={`target-field-${id}`}
                                            options={[]}
                                            isDisabled
                                            defaultValue={{
                                                label: target,
                                                value: target,
                                            }}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            </DataSourceContainer>
        );
    },
);

export default MapFields;
